/**
 * Codex Auth Panel
 *
 * Allows users to connect/disconnect their Codex account.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, type CodexAuthStatus } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  AlertTriangle,
  CheckCircle2,
  Link2,
  Loader2,
  XCircle,
} from 'lucide-react';
import {
  CodexAuthTerminal,
  type CodexTerminalStage,
} from './CodexAuthTerminal';

type AuthWizardState = 'idle' | 'connecting' | 'error';

interface CodexAuthPanelProps {
  onWizardStateChange?: (isWizardActive: boolean) => void;
}

const stageInstructions: Record<CodexTerminalStage, { title: string; description: string }> = {
  starting: {
    title: 'Starting Codex Login',
    description: 'The terminal is preparing the Codex device authentication flow.',
  },
  'device-code': {
    title: 'Enter Device Code',
    description: 'Open the OpenAI URL shown in the terminal and enter the one-time code there.',
  },
  waiting: {
    title: 'Waiting for OpenAI',
    description: 'After you enter the code, Codex will finish authentication automatically.',
  },
  success: {
    title: 'Codex Connected',
    description: 'Your Codex account is connected.',
  },
  error: {
    title: 'Authentication Failed',
    description: 'Cancel and start again. Device codes expire after a short time.',
  },
};

export function CodexAuthPanel({ onWizardStateChange }: CodexAuthPanelProps) {
  const [status, setStatus] = useState<CodexAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [wizardState, setWizardState] = useState<AuthWizardState>('idle');
  const [terminalStage, setTerminalStage] = useState<CodexTerminalStage>('starting');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.codex.getStatus();
      setStatus(response);
    } catch (error) {
      console.error('Failed to fetch Codex status:', error);
      setStatus({
        cliInstalled: false,
        authenticated: false,
        error: 'Failed to check status',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    onWizardStateChange?.(wizardState === 'connecting');
  }, [wizardState, onWizardStateChange]);

  const handleConnect = async () => {
    setErrorMessage(null);
    setTerminalStage('starting');

    try {
      await api.codex.connect();
      setWizardState('connecting');
    } catch (error) {
      console.error('Codex connect preflight failed:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to prepare Codex authentication');
      setWizardState('error');
    }
  };

  const handleCancel = () => {
    setWizardState('idle');
    setTerminalStage('starting');
    setErrorMessage(null);
    void fetchStatus();
  };

  const handleAuthSuccess = async () => {
    await fetchStatus();
    setWizardState('idle');
    window.dispatchEvent(new CustomEvent('codex-auth-changed'));
  };

  const handleDisconnectConfirm = async () => {
    setShowDisconnectConfirm(false);
    setDisconnecting(true);

    try {
      await api.codex.disconnect();
      await fetchStatus();
      window.dispatchEvent(new CustomEvent('codex-auth-changed'));
    } catch (error) {
      console.error('Codex disconnect error:', error);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Checking Codex status...</span>
      </div>
    );
  }

  if (!status?.cliInstalled) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-destructive mt-0.5" />
          <div>
            <h4 className="font-medium text-destructive">Codex CLI Not Installed</h4>
            <p className="text-sm text-destructive/80 mt-1">
              The Codex CLI is not installed on the server. Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status.authenticated && wizardState === 'idle') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-green-800 dark:text-green-200">Codex Connected</h4>
              <p className="text-xs text-green-700/80 dark:text-green-200/60 mt-2">
                Your Codex account is connected. Chat routing to Codex will be enabled separately.
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDisconnectConfirm(true)}
          disabled={disconnecting}
          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect Account'}
        </Button>

        <ConfirmDialog
          isOpen={showDisconnectConfirm}
          onClose={() => setShowDisconnectConfirm(false)}
          onConfirm={handleDisconnectConfirm}
          title="Disconnect Codex Account"
          message="Are you sure you want to disconnect your Codex account? You'll need to reconnect before Codex features can use this account."
          confirmLabel="Disconnect"
          variant="destructive"
        />
      </div>
    );
  }

  if (wizardState === 'connecting') {
    const currentInstruction = stageInstructions[terminalStage];

    return (
      <div className="col-span-3 space-y-4">
        <div className="w-full">
            <div className={`rounded-lg border p-4 ${
              terminalStage === 'success'
                ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10'
                : terminalStage === 'error'
                ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                : 'border-primary-200 bg-primary-50 dark:border-primary-500/30 dark:bg-primary-500/10'
            }`}>
              <div className="flex items-start gap-3">
                <Link2 className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                  terminalStage === 'success'
                    ? 'text-green-600 dark:text-green-400'
                    : terminalStage === 'error'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-primary-600 dark:text-primary-400'
                }`} />
                <div className="flex-1 space-y-2">
                  <h4 className={`font-medium ${
                    terminalStage === 'success'
                      ? 'text-green-800 dark:text-green-200'
                      : terminalStage === 'error'
                      ? 'text-red-800 dark:text-red-200'
                      : 'text-primary-800 dark:text-primary-200'
                  }`}>
                    {currentInstruction.title}
                  </h4>
                  <p className={`text-sm ${
                    terminalStage === 'success'
                      ? 'text-green-700 dark:text-green-200/70'
                      : terminalStage === 'error'
                      ? 'text-red-700 dark:text-red-200/70'
                      : 'text-primary-700 dark:text-primary-200/70'
                  }`}>
                  {currentInstruction.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <CodexAuthTerminal
          onSuccess={handleAuthSuccess}
          onCancel={handleCancel}
          onStageChange={setTerminalStage}
        />
      </div>
    );
  }

  if (wizardState === 'error') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 space-y-4 dark:border-red-500/30 dark:bg-red-500/10">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-red-800 dark:text-red-200">Authentication Failed</h4>
            <p className="text-sm text-red-700 dark:text-red-200/70 mt-1">
              {errorMessage || 'An error occurred during authentication.'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-200/70 dark:hover:bg-red-500/10 dark:hover:text-red-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            variant="outline"
            className="flex-1 border-red-200 text-red-600 hover:bg-red-100 dark:border-red-500/30 dark:text-red-200 dark:hover:bg-red-500/10"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-amber-800 dark:text-amber-200">Codex Not Connected</h4>
            <p className="text-sm text-amber-700 dark:text-amber-200/80 mt-1">
              Connect your Codex account to prepare Codex features for this workspace.
            </p>
            {status.error && (
              <p className="text-xs text-amber-700/80 dark:text-amber-200/60 mt-2">
                {status.error}
              </p>
            )}
          </div>
        </div>
      </div>

      <Button onClick={handleConnect}>
        Connect Codex Account
      </Button>
    </div>
  );
}

export default CodexAuthPanel;
